import { config } from '../config';

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
}

export interface BlogPost {
  id: string;
  user_id: string;
  title: string;
  slug: string;
  category_id?: string;
  content: string;
  excerpt?: string;
  featured_image?: string;
  is_published: boolean;
  is_hidden: boolean;
  is_anonymous: boolean;
  views: number;
  published_at?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  author_name?: string;
  author_username?: string;
  author_profile_image?: string;
  author_about_me?: string;
  category_name?: string;
  category_slug?: string;
}

export interface BlogPostsResponse {
  posts: BlogPost[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateBlogPostRequest {
  title: string;
  slug?: string;
  category_id?: string;
  content: string;
  excerpt?: string;
  featured_image?: string;
  is_published?: boolean;
  is_anonymous?: boolean;
}

export interface UpdateBlogPostRequest extends Partial<CreateBlogPostRequest> {
  is_hidden?: boolean;
}

class BlogService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async getCategories(): Promise<BlogCategory[]> {
    const response = await fetch(`${config.apiUrl}/blog-categories`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    
    return response.json();
  }

  async createCategory(name: string): Promise<BlogCategory> {
    const response = await fetch(`${config.apiUrl}/blog-categories`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ name }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create category');
    }
    
    return response.json();
  }

  async getPosts(params?: {
    category?: string;
    keyword?: string;
    sort?: 'popular' | 'recent' | 'oldest';
    limit?: number;
    offset?: number;
    drafts?: boolean;
  }): Promise<BlogPostsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.category) queryParams.append('category', params.category);
    if (params?.keyword) queryParams.append('keyword', params.keyword);
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.drafts) queryParams.append('drafts', 'true');
    
    const response = await fetch(
      `${config.apiUrl}/blog-posts${queryParams.toString() ? '?' + queryParams.toString() : ''}`,
      {
        headers: params?.drafts ? this.getAuthHeaders() : { 'Content-Type': 'application/json' },
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch blog posts');
    }
    
    return response.json();
  }

  async getPost(id: string, slug?: string): Promise<BlogPost> {
    const url = slug 
      ? `${config.apiUrl}/blog-posts/${id}/${slug}`
      : `${config.apiUrl}/blog-posts/${id}`;
      
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch blog post');
    }
    
    return response.json();
  }

  async createPost(data: CreateBlogPostRequest): Promise<BlogPost> {
    const response = await fetch(`${config.apiUrl}/blog-posts`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create blog post');
    }
    
    return response.json();
  }

  async updatePost(id: string, data: UpdateBlogPostRequest): Promise<BlogPost> {
    const response = await fetch(`${config.apiUrl}/blog-posts/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update blog post');
    }
    
    return response.json();
  }

  async deletePost(id: string): Promise<void> {
    const response = await fetch(`${config.apiUrl}/blog-posts/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete blog post');
    }
  }

  async hidePost(id: string, isHidden: boolean): Promise<BlogPost> {
    const response = await fetch(`${config.apiUrl}/blog-posts/${id}/hide`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ is_hidden: isHidden }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to hide/unhide blog post');
    }
    
    return response.json();
  }

  /**
   * Extract image URLs from HTML content
   */
  extractImagesFromContent(htmlContent: string): string[] {
    const imgRegex = /<img[^>]+src="([^">]+)"/g;
    const images: string[] = [];
    let match;
    
    while ((match = imgRegex.exec(htmlContent)) !== null) {
      images.push(match[1]);
    }
    
    return images;
  }
}

export const blogService = new BlogService();
